import { createActionHelpers } from 'vuex-loading';
import Auth from '../../plugins/auth';
import m from 'moment';

const { startLoading, endLoading } = createActionHelpers({
    moduleName: 'loading',
});

const allCategory = {
    code: '0',
    product_name: 'All Categories',
    tag_id: 0,
};

const default_selection = {
    file_name: 'Sales Report',
    file_type: 'CSV',
    report_type: 'sales_report',

    included_label: {
        filter_name: 'Labels',
        list: [],
        operand: 'labels.id',
        operator: 'includes',
    },
    excluded_label: {
        filter_name: 'Labels',
        list: [],
        operand: 'labels.id',
        operator: 'does_not_include',
    },

    outlets: [],
    sites: {
        filter_name: 'Outlets',
        list: [],
        operand: 'outlet_id',
        operator: 'includes',
    },

    filter_type: ['all_site', 'filter_by_labels', 'filter_by_sites'],
    selected_filter_type: 'all_sites',

    filter_time: ['month', 'date'],
    selected_filter_time: 'month',

    date_filter: {},
    selected_date: null,

    aggregation_type: ['aggregate_data', 'data_by_site'],
    selected_aggregate_type: 'aggregate_data',

    hierachy_filter: ['category', 'subcategory'],
    selected_hierachy_filter: 'category',
    category: allCategory,
    subcategory: [],

    columns: [],
    selected_columns: [],
};

const transformSavedSettings = selection => {
    const dto = selection;
    delete dto.outlets;
    delete dto.filter_type;
    delete dto.filter_time;
    delete dto.aggregation_type;
    delete dto.columns;

    return dto;
};

const state = {
    report_type: '',
    report_type_list: [],
    report: [],

    date: {
        start: m().startOf('month'),
        end: m().endOf('day'),
    },

    category: [],
    subcategory: [],
    saved_settings: [],
    useSavedSettings: false,
    current_time: null,
    selection: { ...default_selection },
};

const getters = {
    reportWizard(state) {
        return state;
    },

    reportTypeList(state) {
        return state.report.map(d => d.report);
    },
};

const mutations = {
    setSelection(state, selection) {
        const report = state.report.find(
            d => d.report === selection.report_type,
        );
        state.selection = {
            ...state.selection,
            ...report,
            ...selection,
        };
        state.useSavedSettings = true;
    },

    setIncludedLabels(state, labels) {
        state.selection.included_label.list = labels;
    },

    setExcludedLabels(state, labels) {
        state.selection.excluded_label.list = labels;
    },

    setSites(state, outlet) {
        state.selection.sites.list = outlet;
    },

    setFileName(state, name) {
        state.selection.file_name = name;
    },

    setColumns(state, col) {
        state.selection.columns = col;
    },

    setCurrentTime(state, time) {
        state.current_time = time;
    },

    setSelectedFilterType(state, type) {
        state.selection.selected_filter_type = type;
    },

    setSelectedFilterTime(state, time) {
        state.selection.selected_filter_time = time;
    },

    setSelectedDate(state, date) {
        state.selection.selected_date = date;
    },

    setSelectedColumns(state, column) {
        state.selection.selected_columns = column;
    },

    setHierachyFilter(state, hierachy) {
        state.selection.selected_hierachy_filter = hierachy;
    },

    setSelectedCategory(state, category) {
        state.selection.category = category;
    },

    setSelectedSubCategory(state, subcategory) {
        state.selection.subcategory = subcategory;
    },

    setCategory(state, category) {
        state.category = category;
    },

    setSubcategory(state, subcategory) {
        state.subcategory = subcategory;
    },

    setSavedSettings(state, settings) {
        state.saved_settings = settings;
    },

    setAggregateType(state, type) {
        state.selection.selected_aggregate_type = type;
        const col = state.selection.columns.filter(d => !d.show_aggregate);
        const aggregated_col = state.selection.columns.filter(
            d => d.show_aggregate && d.show_aggregate === type,
        );
        const result =
            type === 'aggregate_data' ? col : [...aggregated_col, ...col];
        state.selection.selected_columns = result
            .filter(d => {
                if (type !== 'aggregate_data') {
                    return d;
                }

                if (!d.untick_aggregate_data_default) {
                    return d;
                }
            })
            .map(d => d.id);
        state.useSavedSettings = false;
    },

    setReportType(state, type) {
        state.report_type = type;
        const value = state.report.find(d => d.report === type);
        state.selection = { ...state.selection, ...value };
        state.selection.selected_date = state.date;
        state.selection.file_name = type.replaceAll('_', ' ');
        state.selection.selected_filter_time = value.filter_time[0];
    },

    setReport(state, report) {
        state.report = report;
    },
};

const actions = {
    async getReportWizardInfo({ commit, dispatch }) {
        const url = Laroute.route('franchise.report-wizard.info');
        startLoading(dispatch, 'fetch-report-wizard-info');

        try {
            const { data } = await axios.get(url);
            const report_data = data[0];
            commit('setReport', data);
            commit('setReportType', report_data.report);
            commit('setAggregateType', report_data.aggregation_type[0]);
            endLoading(dispatch, 'fetch-report-wizard-info');
            return data;
        } catch (e) {
            endLoading(dispatch, 'fetch-report-wizard-info');
            throw e;
        }
    },

    async getSubCategoryList({ commit, dispatch }, params) {
        const url = Laroute.route('franchise.report-wizard.sub-category');
        startLoading(dispatch, 'fetch-subcategory-list');

        try {
            let options = {
                params: {
                    ...params,
                },
            };

            const { data } = await axios.get(url, options);
            commit('setSubcategory', data.data || []);
            endLoading(dispatch, 'fetch-subcategory-list');
            return data;
        } catch (e) {
            endLoading(dispatch, 'fetch-subcategory-list');
            throw e;
        }
    },

    async getCategoryList({ commit, dispatch }, params) {
        const url = Laroute.route('franchise.report-wizard.category');
        startLoading(dispatch, 'fetch-category-list');

        try {
            let options = {
                params: {
                    ...params,
                },
            };
            const { data } = await axios.get(url, options);
            commit('setCategory', [allCategory, ...data.data]);
            endLoading(dispatch, 'fetch-category-list');
            return data;
        } catch (e) {
            endLoading(dispatch, 'fetch-category-list');
            throw e;
        }
    },

    async getSavedSettingsList({ commit, dispatch }, params) {
        startLoading(dispatch, 'fetch-saved-settings-list');
        const url = Laroute.route('franchise.report-wizard.saved-settings');
        startLoading(dispatch, 'fetch-saved-settings-list');

        try {
            let options = {
                params: {
                    ...params,
                },
            };
            const { data } = await axios.get(url, options);
            commit('setSavedSettings', data);
            endLoading(dispatch, 'fetch-saved-settings-list');
            return data;
        } catch (e) {
            endLoading(dispatch, 'fetch-saved-settings-list');
            throw e;
        }
    },

    async createExport({ commit, dispatch, state }, params) {
        let route = Laroute.route('franchise.report-wizard.export');
        startLoading(dispatch, 'report-wizard-export');

        return axios
            .post(route, params)
            .then(res => {
                const report = state.report[0];
                commit('setReportType', report.report);
                commit('setAggregateType', report.aggregation_type[0]);
                endLoading(dispatch, 'report-wizard-export');

                return res;
            })
            .catch(err => {
                endLoading(dispatch, 'report-wizard-export');
                throw err;
            });
    },

    async createSavedSettings({ commit, dispatch, state }, params) {
        let route = Laroute.route(
            'franchise.report-wizard.create-saved-settings',
        );
        startLoading(dispatch, 'report-wizard-save');

        const dto = transformSavedSettings(state.selection);

        dto.user_id = Auth.user().id;

        return axios
            .post(route, dto)
            .then(res => {
                state.useSavedSettings = false;
                endLoading(dispatch, 'report-wizard-save');

                return res;
            })
            .catch(err => {
                endLoading(dispatch, 'report-wizard-save');
                throw err;
            });
    },

    reset({ commit, state }) {
        state.selection = default_selection;
        state.selection.sites.list = [];
        if (state.report.length) {
            const report = state.report[0];
            commit('setReportType', report.report);
            commit('setAggregateType', report.aggregation_type[0]);
        }
    },

    setSelectedReport({ commit, state }, type) {
        state.selection.report_type = type;
        const index = state.report.findIndex(d => d.report === type);
        const value = state.report[index];
        state.selection = { ...state.selection, ...value };
        state.selection.hierachy_filter = value.hierachy_filter || [
            'category',
            'subcategory',
        ];
        state.selection.selected_date = state.date;
        state.selection.file_name = type.replaceAll('_', ' ');
        commit('setSelectedFilterTime', value.filter_time[0]);
        commit('setAggregateType', value.aggregation_type[0]);
        commit('setReportType', type);
    },

    async useSavedSettings({ commit }, selection) {
        await commit('setSelection', selection);
    },

    async validate({ dispatch }) {
        return Promise.all([
            dispatch('validateColumns'),
            dispatch('validateFilters'),
            dispatch('validateHierarchy'),
        ]);
    },

    validateColumns({ state }) {
        const { selected_columns, columns } = state.selection;
        const unrequired_columns = columns.filter(d => !d.form_required);
        const form_columns = columns.filter(d => d.form_required);

        // validate if unrequired columns is available in selected columns
        const valid = unrequired_columns.filter(d =>
            selected_columns.includes(d.id),
        );

        // validate if form required exists and is added in selected form
        const valid_form = form_columns.length
            ? form_columns.filter(d => selected_columns.includes(d.id))
            : false;
        let result = {
            success: true,
            message: '',
            toSpecification: false,
        };

        if (!valid.length) {
            result.message = 'Columns must have at least 1 selected';
            result.success = false;
        } else if (valid_form && !valid_form.length) {
            result.message = 'Columns must have at least 1 selected';
            result.success = false;
        }
        return result;
    },

    validateFilters({ state }) {
        const {
            selected_filter_type,
            sites,
            included_label,
            excluded_label,
        } = state.selection;
        let result = {
            success: true,
            message: '',
            toSpecification: false,
        };

        switch (selected_filter_type) {
            case 'filter_by_labels':
                if (
                    !included_label.list.length &&
                    !excluded_label.list.length
                ) {
                    result = {
                        message: 'Select at least 1 Include or Exclude Label',
                        success: false,
                        toSpecification: true,
                    };
                }
                break;
            case 'filter_by_sites':
                if (!sites.list.length) {
                    result = {
                        message: 'Select at least 1 Site',
                        success: false,
                        toSpecification: true,
                    };
                }
                break;
            default:
                break;
        }

        return result;
    },

    validateHierarchy({ state }) {
        const {
            hierachy_filter,
            selected_hierachy_filter,
            category,
            subcategory,
        } = state.selection;
        let result = {
            success: true,
            message: '',
            toSpecification: false,
        };

        if (hierachy_filter.length) {
            switch (selected_hierachy_filter) {
                case 'category':
                    if (!category) {
                        result = {
                            message: 'Select a category',
                            success: false,
                            toSpecification: true,
                        };
                    }
                    break;
                case 'subcategory':
                    if (!subcategory.length) {
                        result = {
                            message: 'Select at least 1 subcategory',
                            success: false,
                            toSpecification: true,
                        };
                    }
                    break;
                default:
                    break;
            }
        }

        return result;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
